"use client";

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Search,
  Download,
  ArrowRight,
  X,
  Clock,
  GraduationCap,
  TrendingUp,
  ShieldCheck,
  CircleCheck,
  SlidersHorizontal,
} from "lucide-react";
import { fetchAcademicByName } from "@/helpers/GetAcademics";
import RelatedBlogs from "@/components/diploma/RelatedBlogs";
import UserFormModal from "@/components/UserFormModal";

const getHighlightIcon = (text = "") => {
  const t = text.toLowerCase();
  if (t.includes("year")) return Clock;
  if (t.includes("degree")) return GraduationCap;
  if (t.includes("%") || t.includes("practical")) return TrendingUp;
  if (t.includes("approved") || t.includes("ugc")) return ShieldCheck;
  return CircleCheck;
};

const formatDate = (isoString) => {
  if (!isoString) return "";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const SHEET_TRANSITION_MS = 300;
const DRAG_CLOSE_THRESHOLD = 90; // px dragged down before we treat it as "close"

const ProgramsGrid = ({ category, relatedBlogTitle }) => {
  const [academicsHeading, setAcademicsHeading] = useState("");
  const [academicsSubHeading, setAcademicsSubHeading] = useState("");
  const [courses, setCourses] = useState([]);
  const [schoolFilters, setSchoolFilters] = useState([]);
  const [durationFilters, setDurationFilters] = useState([]);
  const [streamFilters, setStreamFilters] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [selectedStreams, setSelectedStreams] = useState([]);
  const [activeTab, setActiveTab] = useState("all"); // "all" | "new" — no data source for "new" yet
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [shouldDownload, setShouldDownload] = useState(false);

  // Mobile filter sheet: separate "mounted" vs "open" state so we can play
  // an exit transition before removing the sheet from the DOM.
  const [isSheetMounted, setIsSheetMounted] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartYRef = useRef(0);
  const closeTimeoutRef = useRef(null);

  const handleCourseDownload = useCallback((course) => {
    setSelectedCourse({ ...course, BrochurePdf: course.Brochure_Pdf });
    setShouldDownload(true);
    setIsDownloadModalOpen(true);
  }, []);


const CDN_HOST = "https://cdn.msu.edu.in";
const S3_HOST = "https://msu-website-all-objects.s3.ap-south-1.amazonaws.com";
// ...inside your component
const downloadBrochureFile = useCallback(async (course) => {
  const pdfUrl = course?.BrochurePdf?.url;
  if (!pdfUrl) {
    alert("Brochure is not available for download.");
    return;
  }

  // Swap CDN host for the S3 host so the fetch isn't blocked by CORS
  const downloadUrl = pdfUrl.startsWith(CDN_HOST)
    ? pdfUrl.replace(CDN_HOST, S3_HOST)
    : pdfUrl;

  try {
    const response = await fetch(downloadUrl);
    if (!response.ok) throw new Error(`Failed to fetch brochure: ${response.status}`);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = course.BrochurePdf.name || "brochure.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Brochure download failed:", err);
    alert("Something went wrong while downloading the brochure.");
  }
}, []);
  const handleFormSuccess = useCallback(() => {
    if (shouldDownload && selectedCourse) {
      downloadBrochureFile(selectedCourse);
      setShouldDownload(false);
      setSelectedCourse(null);
    }
  }, [selectedCourse, shouldDownload, downloadBrochureFile]);

  const getCourseImageUrl = (course) => {
    const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL;

    const imageUrl = course?.featured_image?.url || "";

    if (!imageUrl) return "";

    return imageUrl.startsWith("http") ? imageUrl : `${serverUrl}${imageUrl}`;
  };

  useEffect(() => {
    async function getData() {
      setLoading(true);
      const res = await fetchAcademicByName(category);

      if (res && res.data && res.data[0]) {
        setAcademicsHeading(res.data[0].Academics_Heading || "");
        setAcademicsSubHeading(res.data[0].Academics_SubHeading || "");
        setCourses(res.data[0].Course || []);
        setSchoolFilters(res.data[0].School_Filters || []);
        setDurationFilters(res.data[0].Duration_Filters || []);
        setStreamFilters(res.data[0].Stream_Filters || []);
        setLastUpdated(res.data[0].updatedAt || null);
        setRelatedBlogs(res.data[0].Blogs || [])
      }
      setLoading(false);
    }
    getData();
  }, [category]);


  const openMobileFilter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setDragY(0);
    setIsSheetMounted(true);
    // Mount first (offscreen), then flip to open on the next frame so the
    // transform transition actually has a "from" state to animate from.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsSheetOpen(true));
    });
  };

  const closeMobileFilter = useCallback(() => {
    setIsSheetOpen(false);
    setDragY(0);
    closeTimeoutRef.current = setTimeout(() => {
      setIsSheetMounted(false);
    }, SHEET_TRANSITION_MS);
  }, []);

  // Lock body scroll while the mobile filter sheet is mounted
  useEffect(() => {
    if (isSheetMounted) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isSheetMounted]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  // Drag-to-dismiss on the handle: follows the pointer while dragging down,
  // snaps back if released above the threshold, closes if released below it.
  const handleDragStart = (clientY) => {
    dragStartYRef.current = clientY;
    setIsDragging(true);
  };

  const handleDragMove = (clientY) => {
    const delta = clientY - dragStartYRef.current;
    setDragY(Math.max(0, delta));
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (dragY > DRAG_CLOSE_THRESHOLD) {
      closeMobileFilter();
    } else {
      setDragY(0);
    }
  };

  const onHandlePointerDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientY);
  };
  const onHandlePointerMove = (e) => {
    if (!isDragging) return;
    handleDragMove(e.clientY);
  };
  const onHandlePointerUp = () => {
    if (!isDragging) return;
    handleDragEnd();
  };

  const onHandleTouchStart = (e) => {
    handleDragStart(e.touches[0].clientY);
  };
  const onHandleTouchMove = (e) => {
    if (!isDragging) return;
    handleDragMove(e.touches[0].clientY);
  };
  const onHandleTouchEnd = () => {
    if (!isDragging) return;
    handleDragEnd();
  };

  const toggleSchool = (schoolName) => {
    setSelectedSchools((prev) =>
      prev.includes(schoolName)
        ? prev.filter((s) => s !== schoolName)
        : [...prev, schoolName],
    );
  };

  const toggleDuration = (duration) => {
    setSelectedDurations((prev) =>
      prev.includes(duration)
        ? prev.filter((d) => d !== duration)
        : [...prev, duration],
    );
  };

  const toggleStream = (streamName) => {
    setSelectedStreams((prev) =>
      prev.includes(streamName)
        ? prev.filter((s) => s !== streamName)
        : [...prev, streamName],
    );
  };

  // Course doesn't have a dedicated Duration field yet — matching against
  // Course_Highlights text is a stopgap until Duration exists on Course itself.
  const courseMatchesDuration = (course, duration) => {
    const highlights = course.Course_Highlights || [];
    return highlights.some((h) =>
      h.Highlight?.toLowerCase().includes(duration.toLowerCase()),
    );
  };

  // No Stream field exists on Course yet, and no mapping from school -> stream
  // is defined anywhere in the schema. This always matches until that field
  // is added — selecting a Stream checkbox currently has no filtering effect.
  const courseMatchesStream = () => true;

  const schoolCounts = useMemo(() => {
    const counts = {};
    schoolFilters.forEach((s) => {
      counts[s.School_Name] = courses.filter(
        (c) => c.school?.name === s.School_Name,
      ).length;
    });
    return counts;
  }, [courses, schoolFilters]);

  const durationCounts = useMemo(() => {
    const counts = {};
    durationFilters.forEach((d) => {
      counts[d.Duration] = courses.filter((c) =>
        courseMatchesDuration(c, d.Duration),
      ).length;
    });
    return counts;
  }, [courses, durationFilters]);

  const streamCounts = useMemo(() => {
    const counts = {};
    streamFilters.forEach((s) => {
      counts[s.Stream_Name] = courses.length; // no real per-stream count source yet
    });
    return counts;
  }, [courses, streamFilters]);

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.Course_Name.toLowerCase().includes(
      searchQuery.toLowerCase(),
    );

    const matchesSchool =
      selectedSchools.length === 0 ||
      selectedSchools.includes(course.school?.name);

    const matchesDuration =
      selectedDurations.length === 0 ||
      selectedDurations.some((d) => courseMatchesDuration(course, d));

    const matchesStream =
      selectedStreams.length === 0 ||
      selectedStreams.some((s) => courseMatchesStream(course, s));

    return matchesSearch && matchesSchool && matchesDuration && matchesStream;
  });

  const activePills = [
    ...selectedSchools.map((v) => ({ type: "school", value: v })),
    ...selectedDurations.map((v) => ({ type: "duration", value: v })),
    ...selectedStreams.map((v) => ({ type: "stream", value: v })),
  ];

  const activeFilterCount = activePills.length;

  const removePill = (pill) => {
    if (pill.type === "school") toggleSchool(pill.value);
    if (pill.type === "duration") toggleDuration(pill.value);
    if (pill.type === "stream") toggleStream(pill.value);
  };

  const clearAllFilters = () => {
    setSelectedSchools([]);
    setSelectedDurations([]);
    setSelectedStreams([]);
  };

  // Shared filter body — rendered inside the desktop sidebar AND the mobile sheet
  const FilterSections = () => (
    <>
      <div>
        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2 tracking-wide">
          Schools
        </h4>
        <div className="border border-gray-200 rounded-md p-4 space-y-3">
          {schoolFilters.map((s) => (
            <label
              key={s.id}
              className="flex items-center justify-between text-sm text-gray-700 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedSchools.includes(s.School_Name)}
                  onChange={() => toggleSchool(s.School_Name)}
                  className="rounded-xs border-gray-300 accent-[#081E2D]"
                />
                {s.School_Name}
              </span>
              <span className="text-gray-400 text-xs">
                {schoolCounts[s.School_Name] || 0}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2 tracking-wide">
          Duration
        </h4>
        <div className="border border-gray-200 rounded-md p-4 space-y-3">
          {durationFilters.map((d) => (
            <label
              key={d.id}
              className="flex items-center justify-between text-sm text-gray-700 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedDurations.includes(d.Duration)}
                  onChange={() => toggleDuration(d.Duration)}
                  className="rounded-xs border-gray-300 accent-[#081E2D]"
                />
                {d.Duration}
              </span>
              <span className="text-gray-400 text-xs">
                {durationCounts[d.Duration] || 0}
              </span>
            </label>
          ))}
        </div>
      </div>

      {streamFilters.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2 tracking-wide">
            Stream
          </h4>
          <div className="border border-gray-200 rounded-md p-4 space-y-3">
            {streamFilters.map((s) => (
              <label
                key={s.id}
                className="flex items-center justify-between text-sm text-gray-700 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedStreams.includes(s.Stream_Name)}
                    onChange={() => toggleStream(s.Stream_Name)}
                    className="rounded-xs border-gray-300 accent-[#081E2D]"
                  />
                  {s.Stream_Name}
                </span>
                <span className="text-gray-400 text-xs">
                  {streamCounts[s.Stream_Name] || 0}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="py-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#081E2D] mx-auto"></div>
        <p className="mt-4 text-gray-500">Loading programs...</p>
      </div>
    );
  }

  return (
    <section className="bg-white py-[50px] md:py-[80px]">
      <div className="page-container mx-auto">
        {academicsHeading && (
          <h2
            className="text-[24px] lg:text-[42px] mb-4.5"
            style={{ fontFamily: "saans-bold" }}
          >
            {academicsHeading}
          </h2>
        )}
        {academicsSubHeading && (
          <p className="text-[16px] mb-15 md:w-[80%]">{academicsSubHeading}</p>
        )}

        {/* Active filter pills */}
        {activePills.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {activePills.map((pill) => (
              <span
                key={`${pill.type}-${pill.value}`}
                className="flex items-center gap-2 bg-blue-50 text-blue-700 text-sm rounded-full pl-3 pr-2 py-1"
              >
                {pill.value}
                <button
                  type="button"
                  onClick={() => removePill(pill)}
                  className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-700 text-white hover:bg-blue-800"
                  aria-label={`Remove ${pill.value} filter`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar filters — desktop only, mobile uses the sheet below */}
          <aside className="hidden md:block md:w-[30%] space-y-6">
            <FilterSections />
            {
              relatedBlogs.length > 0 && (
                <aside>
                  <RelatedBlogs blogs={relatedBlogs} title={relatedBlogTitle}/>
                </aside>
              )
            }
          </aside>


          {/* Course list */}
          <div className="w-full md:w-[70%]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <p className="text-sm text-gray-500">
                Showing {filteredCourses.length} of {courses.length} listings
                {lastUpdated && ` · Last updated ${formatDate(lastUpdated)}`}
              </p>

              <div className="flex items-center gap-3">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2  h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search programmes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border border-gray-300 pl-9 pr-4 py-2 bg-[rgba(249,250,251,1)] rounded-full focus:outline-none text-sm w-[200px] sm:w-[274px]"
                  />
                </div>

                {/* Mobile-only filters trigger */}
                <button
                  type="button"
                  onClick={openMobileFilter}
                  className="md:hidden relative flex items-center gap-2 bg-[#081E2D] text-white text-sm font-medium rounded-full px-4 py-2.5 shrink-0 transition-transform active:scale-95"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#F59E0B] text-[#081E2D] text-[11px] font-semibold">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {filteredCourses.map((course) => {
                const imageUrl = getCourseImageUrl(course);
                const schoolName = course.school?.name;
                const highlights = course.Course_Highlights || [];
                const brochureUrl = course.Brochure_Pdf?.url;
                const courseContent = course.course_content || null;
                // const courseDetails = course.Course_Details || [];


                return (
                  <div
                    key={course.id}
                    className="group flex flex-col [@media(min-width:1100px)]:flex-row gap-0 bg-white border-[1.13px] border-[#F3F4F6] rounded-xs overflow-hidden hover:shadow-md transition"
                  >
                    <Link href={course.Course_Link || "#"} className="relative">
                      {imageUrl ? (
                        <Image
                          src={imageUrl}
                          alt={course.Course_Name || "Course image"}
                          width={370}
                          height={330}
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, 220px"
                        />
                      ) : (
                        <div className="flex h-full min-h-[180px] items-center justify-center text-sm text-gray-500">
                          Image unavailable
                        </div>
                      )}
                    </Link>

                    <div className="flex flex-col p-5 flex-1">
                      <div>
                        <Link href={course.Course_Link || "#"}>
                          <h3 className="text-[16px] xl:text-[18px] font-semibold text-black hover:underline">
                            {course.Course_Name}
                          </h3>
                        </Link>
                        {schoolName && (
                          <p
                            className="text-[13px] xl:text-[15px] text-gray-500 mt-2"
                            style={{ fontFamily: "saans-medium" }}
                          >
                            {schoolName}
                          </p>
                        )}

                        {highlights.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3 mb-5">
                            {highlights.map((h) => {
                              const Icon = getHighlightIcon(h.Highlight);
                              return (
                                <span
                                  key={h.id}
                                  className="flex items-center gap-1 text-xs text-gray-600 bg-[#F9FAFB] border border-gray-200 rounded-full px-2.5 py-1"
                                >
                                  <Icon className="w-3.5 h-3.5" />
                                  {h.Highlight}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {courseContent && (
                          <div className="mt-2">
                            <div className="text-[16px] text-[#333] mb-4">
                              <p className="line-clamp-2">{courseContent}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <hr className="mb-5 border-gray-300" />
                      <div className="flex flex-col sm:flex-row gap-3">
                        {brochureUrl ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCourseDownload(course);
                            }}
                            className="flex items-center justify-center gap-2 border border-gray-300 rounded-xs px-4 py-2.5 text-sm font-medium text-black hover:bg-gray-50 transition flex-1"
                          >
                            Download Brochure <Download className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="flex items-center justify-center gap-2 border border-gray-200 rounded-xs px-4 py-2.5 text-sm font-medium text-gray-400 cursor-not-allowed flex-1">
                            Brochure Unavailable
                          </span>
                        )}

                        <Link
                          href="https://application.msu.edu.in/campus-programme-registration"
                          target="_blank"
                          className="flex items-center justify-center gap-2 bg-[#F59E0B] rounded-xs px-4 py-2.5 text-sm font-medium text-white hover:bg-[#d98a08] transition flex-1"
                        >
                          Enrol Now <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredCourses.length === 0 && (
                <div className="col-span-full py-20 text-center text-gray-500">
                  No courses found matching
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Related Blogs */}
        {
          relatedBlogs.length > 0 && (
            <aside className="mt-3 mb-3 md:hidden">
              <RelatedBlogs blogs={relatedBlogs} title={relatedBlogTitle}/>
            </aside>
          )
        }

      </div>

      {/* Mobile filter sheet — full-screen panel matching the provided design */}
      {isSheetMounted && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 transition-opacity ease-out"
            style={{
              transitionDuration: `${SHEET_TRANSITION_MS}ms`,
              opacity: isSheetOpen ? 1 : 0,
            }}
            onClick={closeMobileFilter}
          />

          {/* Panel */}
          <div
            className="absolute inset-x-0 bottom-0 max-h-[85vh] w-full bg-white shadow-xl rounded-t-3xl flex flex-col"
            style={{
              transform: `translateY(${isSheetOpen ? "0%" : "100%"}) translateY(${dragY}px)`,
              transition: isDragging
                ? "none"
                : `transform ${SHEET_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
            }}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onPointerLeave={onHandlePointerUp}
          >
            {/* Drag handle — press and drag down (mouse or touch) to dismiss */}
            <button
              type="button"
              onClick={closeMobileFilter}
              onPointerDown={onHandlePointerDown}
              onTouchStart={onHandleTouchStart}
              onTouchMove={onHandleTouchMove}
              onTouchEnd={onHandleTouchEnd}
              aria-label="Close filters"
              className="flex justify-center pt-3 pb-1 shrink-0 w-full touch-none cursor-grab active:cursor-grabbing"
            >
              <span className="w-10 h-1.5 rounded-full bg-gray-300 transition-colors hover:bg-gray-400" />
            </button>

            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
              <h3 className="text-base font-semibold text-black">Filters</h3>
              <button
                type="button"
                onClick={closeMobileFilter}
                aria-label="Close filters"
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              <FilterSections />
            </div>

            <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-100 shrink-0 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <button
                type="button"
                onClick={clearAllFilters}
                className="flex-1 text-sm font-medium text-gray-600 border border-gray-300 rounded-full px-4 py-2.5 hover:bg-gray-50 transition"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={closeMobileFilter}
                className="flex-1 text-sm font-medium text-white bg-[#081E2D] rounded-full px-4 py-2.5 hover:bg-[#0c2b40] transition"
              >
                Show {filteredCourses.length} results
              </button>
            </div>
          </div>
        </div>
      )}

      <UserFormModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onSuccess={handleFormSuccess}
        shouldDownload={shouldDownload}
      />

    </section>
  );
};

export default ProgramsGrid;